<template>
  <div class="serial-console-page">
    <button
      v-if="connectionState === 'disconnected' || connectionState === 'error'"
      type="button"
      class="serial-console-page__reconnect-btn"
      @click="reconnectNow"
    >
      Reconnect now
    </button>
    <div
      ref="screenRef"
      class="serial-console-page__screen"
      tabindex="0"
      @click="focusInput"
    ></div>
  </div>
</template>

<script setup>
import { onBeforeUnmount, onMounted, ref } from "vue"
import { Terminal } from "xterm"
import { FitAddon } from "@xterm/addon-fit"
import "xterm/css/xterm.css"

const props = defineProps({
  vmId: { type: [String, Number], required: true },
  title: { type: String, default: "Serial Console" },
  wsUrl: { type: String, required: true }
})

const connectionState = ref("connecting")
const screenRef = ref(null)
const reconnectAt = ref(0)
const reconnectCountdown = ref(0)

let socket = null
let terminal = null
let fitAddon = null
let resizeObserver = null
let reconnectTimer = null
let reconnectCountdownTimer = null
let reconnectAttempt = 0
let manualClose = false

function emitParentStatus(state) {
  const mapping = {
    connected: { badgeText: "Connected", badgeTone: "normal" },
    reconnecting: { badgeText: "Reconnecting", badgeTone: "warn" },
    connecting: { badgeText: "Connecting", badgeTone: "warn" },
    disconnected: { badgeText: "Disconnected", badgeTone: "error" },
    error: { badgeText: "Disconnected", badgeTone: "error" }
  }
  const payload = mapping[state] || mapping.connecting
  window.parent?.postMessage?.({
    type: "scc:console-status",
    ...payload
  }, "*")
}

function setConnectionState(state) {
  connectionState.value = state
  emitParentStatus(state)
}

function focusInput() {
  terminal?.focus()
}

function sendInput(data) {
  if (!socket || socket.readyState !== window.WebSocket.OPEN || !data) return
  socket.send(data)
}

function clearReconnectTimers() {
  if (reconnectTimer) {
    window.clearTimeout(reconnectTimer)
    reconnectTimer = null
  }
  if (reconnectCountdownTimer) {
    window.clearInterval(reconnectCountdownTimer)
    reconnectCountdownTimer = null
  }
  reconnectAt.value = 0
  reconnectCountdown.value = 0
}

function updateReconnectCountdown() {
  if (!reconnectAt.value) {
    reconnectCountdown.value = 0
    return
  }
  const remainingMs = reconnectAt.value - Date.now()
  reconnectCountdown.value = remainingMs > 0 ? Math.ceil(remainingMs / 1000) : 0
}

function scheduleReconnect() {
  if (manualClose || !props.wsUrl) return

  const delayMs = Math.min(1000 * (2 ** reconnectAttempt), 10000)
  reconnectAttempt += 1
  setConnectionState("reconnecting")
  reconnectAt.value = Date.now() + delayMs
  updateReconnectCountdown()

  if (!reconnectCountdownTimer) {
    reconnectCountdownTimer = window.setInterval(updateReconnectCountdown, 250)
  }
  reconnectTimer = window.setTimeout(() => {
    reconnectTimer = null
    connectSocket()
  }, delayMs)
}

function attachSocketHandlers(ws) {
  ws.addEventListener("open", () => {
    setConnectionState("connected")
    reconnectAttempt = 0
    clearReconnectTimers()
    syncSerialSize()
  })

  ws.addEventListener("message", (event) => {
    if (typeof event.data === "string") {
      terminal.write(event.data)
      return
    }
    if (event.data instanceof ArrayBuffer) {
      terminal.write(new Uint8Array(event.data))
      return
    }
    if (event.data instanceof Blob) {
      event.data.arrayBuffer().then((buffer) => {
        terminal?.write(new Uint8Array(buffer))
      }).catch(() => {
        // ignore malformed payload
      })
    }
  })

  ws.addEventListener("close", () => {
    if (manualClose) return
    if (connectionState.value !== "error") {
      setConnectionState("disconnected")
    }
    scheduleReconnect()
  })

  ws.addEventListener("error", () => {
    if (manualClose) return
    setConnectionState("error")
  })
}

function connectSocket() {
  if (!props.wsUrl) {
    setConnectionState("error")
    terminal?.writeln("[serial-console] WebSocket URL is not available.")
    return
  }

  clearReconnectTimers()

  if (socket) {
    try {
      socket.close()
    } catch (_) {
      // ignore
    }
    socket = null
  }

  setConnectionState(reconnectAttempt > 0 ? "reconnecting" : "connecting")
  socket = new window.WebSocket(props.wsUrl)
  attachSocketHandlers(socket)
}

function reconnectNow() {
  reconnectAttempt = 0
  clearReconnectTimers()
  connectSocket()
}

onMounted(() => {
  emitParentStatus(connectionState.value)
  terminal = new Terminal({
    cols: 80,
    rows: 24,
    cursorBlink: true,
    fontFamily: '"SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace',
    fontSize: 14,
    lineHeight: 1.45,
    theme: {
      background: "#0b1220",
      foreground: "#d5e2ff",
      cursor: "#d5e2ff",
      selectionBackground: "rgba(132, 156, 206, 0.28)"
    }
  })
  fitAddon = new FitAddon()
  terminal.loadAddon(fitAddon)
  terminal.open(screenRef.value)
  syncSerialSize()
  terminal.focus()
  terminal.onData((data) => {
    sendInput(data)
  })

  resizeObserver = new ResizeObserver(() => {
    syncSerialSize()
  })
  if (screenRef.value) {
    resizeObserver.observe(screenRef.value)
  }
  window.addEventListener("resize", handleWindowResize)

  connectSocket()
})

onBeforeUnmount(() => {
  manualClose = true
  clearReconnectTimers()
  resizeObserver?.disconnect()
  window.removeEventListener("resize", handleWindowResize)
  if (socket) {
    socket.close()
  }
  terminal?.dispose()
})

function handleWindowResize() {
  syncSerialSize()
}

function syncSerialSize() {
  if (!terminal) return

  const dims = fitAddon?.proposeDimensions?.()
  const rows = Math.max(10, dims?.rows || terminal.rows || 24)

  // Most serial consoles are effectively fixed at 80 columns.
  // Keeping cols at 80 avoids redraw/overwrite issues past column 80.
  terminal.resize(80, rows)
}
</script>

<style scoped>
.serial-console-page {
  height: 100%;
  min-height: 100%;
  display: flex;
  flex-direction: column;
  background: #0b1220;
  color: #d5e2ff;
  overflow: hidden;
}

.serial-console-page__reconnect-btn {
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 5;
  border: 1px solid rgba(143, 168, 224, 0.5);
  border-radius: 8px;
  padding: 4px 10px;
  color: #d5e2ff;
  background: rgba(26, 37, 61, 0.88);
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
}

.serial-console-page__screen {
  position: relative;
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
  padding: 0;
  margin: 0;
  outline: none;
}

.serial-console-page__screen :deep(.xterm) {
  height: 100%;
  width: 100%;
  margin: 0 !important;
  padding: 0 !important;
}

.serial-console-page__screen :deep(.xterm-screen) {
  height: 100%;
  top: 0 !important;
  padding-top: 0 !important;
}

.serial-console-page__screen :deep(.xterm-viewport) {
  inset: 0;
  width: 100% !important;
  height: 100% !important;
  overflow-y: auto !important;
  top: 0 !important;
  padding-top: 0 !important;
  padding: 0 !important;
}

.serial-console-page__screen :deep(.xterm-scroll-area) {
  top: 0 !important;
}

.serial-console-page__screen :deep(.xterm-rows) {
  padding-top: 0 !important;
  margin-top: 0 !important;
}
</style>
